import { Component, EventEmitter, inject, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { popupNotification, Notification } from '../notification.model';
import { NotificationsListComponent } from '../notifications-list/notifications-list';
import { Router, RouterModule } from '@angular/router';
import { NotificationCommunicationService } from '../../../services/notification-communication.service';
import { NotificationService } from '../../../services/notifications/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { formatRelativeDate } from '../../../utils/format.utils';

@Component({
  selector: 'app-notifications-popup',
  standalone: true,
  imports: [CommonModule, NotificationsListComponent, RouterModule],
  templateUrl: './notifications-popup.html',
  styleUrls: ['./notifications-popup.scss'],
})
export class NotificationsPopup implements OnInit, OnDestroy {
  constructor() {}
  private route = inject(Router);
  private notificationService = inject(NotificationCommunicationService);
  private notificationApiService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  @Input() notifications: popupNotification[] = [];
  @Output() closePopup = new EventEmitter<void>();
  @Output() markAllAsRead = new EventEmitter<void>();

  activeTab: string = 'all';
  showFullList = false;
  private isNavigating = false; // Prevent multiple rapid clicks
  apiNotifications: popupNotification[] = [];
  isLoading = false;

  get filteredNotifications() {
    const notificationsToUse = this.apiNotifications.length > 0 ? this.apiNotifications : this.notifications;
    if (this.activeTab === 'all') return notificationsToUse;
    return notificationsToUse?.filter((n) => n.type === this.activeTab);
  }
  //  allNotifications = [
  //   { id: 1, message: 'New message from support', time: '2 min ago', unread: true, type: 'Message' },
  //   { id: 2, message: 'System maintenance scheduled', time: '1 hr ago', unread: false, type: 'Alert' },
  //   { id: 3, message: 'Claim request approved', time: 'Yesterday', unread: true, type: 'Alert' }
  // ];

  allNotifications: Notification[] = [];

  /** Skeleton rows while popup list loads — keep count in sync with typical first fetch (limit 10). */
  readonly skeletonRows = [0, 1, 2, 3, 4];

  ngOnInit() {
    this.loadNotifications();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadNotifications() {
    this.isLoading = true;
    this.notificationApiService
      .getNotifications({ limit: 10, offset: 0 }, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.allNotifications = this.mapApiNotificationsToNotifications(
            response.notifications,
          );
          this.apiNotifications = this.mapToPopupNotifications(response.notifications);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading notifications:', err);
          this.isLoading = false;
        },
      });
  }

  private mapApiNotificationsToNotifications(apiNotifications: any[]): Notification[] {
    return apiNotifications.map((apiNotif) => ({
      id: apiNotif.id,
      message: apiNotif.title,
      date: formatRelativeDate(apiNotif.created_at),
      sender: apiNotif.author_name || 'Admin',
      status: apiNotif.is_read ? 'read' : 'unread',
      type: this.mapCategoryToType(apiNotif.category),
      priority: apiNotif.priority,
      category: apiNotif.category,
      actionRequired: apiNotif.action_required,
      details: apiNotif.details
        ? {
            currentBalance: '',
            minMargin: '',
            tradingCapital: '',
            description: apiNotif.details,
          }
        : undefined,
      title: apiNotif.title,
      is_read: apiNotif.is_read,
      created_at: apiNotif.created_at,
      updated_at: apiNotif.updated_at,
      author_name: apiNotif.author_name,
    }));
  }

  private mapToPopupNotifications(apiNotifications: any[]): popupNotification[] {
    return apiNotifications.map((apiNotif) => ({
      id: apiNotif.id,
      title: apiNotif.title,
      time: formatRelativeDate(apiNotif.created_at),
      type: this.mapCategoryToPopupType(apiNotif.category),
      read: apiNotif.is_read,
    }));
  }

  private mapCategoryToType(category: string): 'Message' | 'Alert' | 'Information' {
    const categoryLower = category?.toLowerCase() || '';
    if (categoryLower === 'security' || categoryLower === 'trading') {
      return 'Alert';
    }
    if (categoryLower === 'support' || categoryLower === 'documents') {
      return 'Message';
    }
    return 'Information';
  }

  private mapCategoryToPopupType(category: string): string {
    const categoryLower = category?.toLowerCase() || '';
    if (categoryLower === 'security' || categoryLower === 'trading') {
      return 'alerts';
    }
    if (categoryLower === 'support' || categoryLower === 'documents') {
      return 'messages';
    }
    return 'updates';
  }

  get messageCount(): number {
    const notificationsToUse = this.apiNotifications.length > 0 ? this.apiNotifications : this.notifications;
    return notificationsToUse?.filter((n) => n.type === 'messages' && !n.read).length || 0;
  }

  get alertCount(): number {
    const notificationsToUse = this.apiNotifications.length > 0 ? this.apiNotifications : this.notifications;
    return notificationsToUse?.filter((n) => n.type === 'alerts' && !n.read).length || 0;
  }

  get hasUnreadNotifications(): boolean {
    const notificationsToUse = this.apiNotifications.length > 0 ? this.apiNotifications : this.notifications;
    return notificationsToUse?.some((n) => !n.read) || false;
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  onMarkAllAsRead() {
    this.notificationApiService.markAllAsRead().subscribe({
      next: () => {
        // Update local state
        this.apiNotifications.forEach((n) => (n.read = true));
        this.allNotifications.forEach((n) => {
          n.status = 'read';
          n.is_read = true;
        });
        // Emit event to parent (header) to refresh unread count
        this.markAllAsRead.emit();
      },
      error: (err) => {
        console.error('Error marking all notifications as read:', err);
      },
    });
  }

  close() {
    this.closePopup.emit();
  }
  openFullList() {
    this.close();

    this.notificationService.requestShowList();
    // Then navigate to full list view
    this.route.navigate(['/main-notifications']);
  }

  closeFullList() {
    this.showFullList = false;
  }

  onNotificationClick(note: popupNotification, event?: Event) {
    // Prevent multiple rapid clicks
    if (this.isNavigating) {
      return;
    }

    // Prevent event propagation to avoid conflicts with document click listener
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isNavigating = true;

    // Mark as read if unread
    if (!note.read) {
      this.notificationApiService.markAsRead(note.id).subscribe({
        next: () => {
          note.read = true;
          const fullNote = this.allNotifications.find((n) => n.id === note.id);
          if (fullNote) {
            fullNote.status = 'read';
            fullNote.is_read = true;
          }
        },
        error: (err) => {
          console.error('Error marking notification as read:', err);
        },
      });
    }

    // Close the popup first - emit the event immediately
    this.closePopup.emit();

    // Check if we're already on the main-notifications route
    const isAlreadyOnNotificationsPage = this.route.url.includes('/main-notifications');

    // Use setTimeout to ensure popup closes before navigation
    // This prevents race conditions with the document click listener
    setTimeout(() => {
      const notificationData = this.convertToNotification(note);

      if (isAlreadyOnNotificationsPage) {
        // If already on the notifications page, use service to update detail view directly
        // This avoids unnecessary navigation and updates the component immediately
        this.notificationService.selectNotification(notificationData);
        this.isNavigating = false;
      } else {
        // Normal navigation when not on the page
        this.route
          .navigate(['/main-notifications'], {
            state: {
              selectedNotification: notificationData,
              fromPopup: true,
            },
          })
          .then(() => {
            // Reset navigation flag after navigation completes
            setTimeout(() => {
              this.isNavigating = false;
            }, 500);
          })
          .catch(() => {
            // Reset flag even if navigation fails
            this.isNavigating = false;
          });
      }
    }, 0);
  }

  private convertToNotification(popupNote: popupNotification): Notification {
    // Find matching notification from allNotifications or create a basic one
    const fullNotification = this.allNotifications.find((n) => n.id === popupNote.id);

    if (fullNotification) {
      return fullNotification;
    }

    // If not found, create a basic notification from popup data
    return {
      id: popupNote.id,
      message: popupNote.title,
      date: popupNote.time,
      sender: 'System',
      status: popupNote.read ? 'read' : 'unread',
      type: this.mapTypeToNotificationType(popupNote.type),
      details: {
        currentBalance: '₹ 0.00',
        minMargin: '₹ 0.00',
        tradingCapital: '₹ 0.00',
        description: popupNote.title,
      },
    };
  }

  private mapTypeToNotificationType(type: string): 'Message' | 'Alert' | 'Information' {
    if (type.toLowerCase() === 'messages' || type.toLowerCase() === 'message') {
      return 'Message';
    } else if (type.toLowerCase() === 'alerts' || type.toLowerCase() === 'alert') {
      return 'Alert';
    }
    return 'Information';
  }
}
